import React, { useState } from 'react';
import { colors } from '../utils/colors';
import type { Teacher, ActiveTab } from '../utils/types';
import BottomNav from './BottomNav';

interface Props {
  teacher: Teacher;
  onBack: () => void;
  onLogout: () => void;
  onNavigate: (tab: ActiveTab) => void;
}

const formatDob = (dob?: string) => {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const capitalize = (s?: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—';

const Row: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', gap: 2,
    padding: '12px 0', borderBottom: `1px solid ${colors.borderLight}`,
  }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: colors.textLight, letterSpacing: '0.6px' }}>
      {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 500, color: colors.text }}>
      {value || '—'}
    </div>
  </div>
);

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: colors.primary,
    letterSpacing: '0.8px', marginTop: 20, marginBottom: 4,
  }}>
    {title}
  </div>
);

const ProfileScreen: React.FC<Props> = ({ teacher, onLogout, onNavigate }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const isSupervisor = teacher.role === 'supervisor';
  const roleLabel = isSupervisor
    ? 'SUPERVISOR'
    : teacher.role === 'admin'
    ? 'ADMIN'
    : teacher.role === 'class_teacher'
    ? 'CLASS TEACHER'
    : 'SUBJECT TEACHER';
  const classLabel = teacher.className
    ? `${teacher.className}${teacher.sectionName ? ` · Sec ${teacher.sectionName}` : ''}`
    : null;

  return (
    <div style={{
      height: '100dvh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* ── Fixed header ── */}
      <div style={{
        padding: '30px 20px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderLight}`,
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: colors.text }}>
          Profile
        </div>

        {/* Right side: person icon + sign-out icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={colors.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg> */}

          {/* Sign-out icon button */}
          <button
            onClick={() => setShowConfirm(true)}
            title="Sign Out"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke={colors.danger} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '0 20px 100px',   // 100px bottom padding clears the nav bar
      }}>
        {/* Avatar + name card */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '20px 0 16px',
          borderBottom: `1px solid ${colors.borderLight}`,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: isSupervisor ? '#7C3AED' : '#C4933A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 800, color: '#fff', flexShrink: 0,
            position: 'relative',
          }}>
            {teacher.name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')}
            {isSupervisor && (
              <div style={{
                position: 'absolute', bottom: -2, right: -2,
                width: 20, height: 20, borderRadius: '50%',
                background: '#FFD700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, border: '2px solid #fff',
              }}>
                ★
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>
              {teacher.name}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              <div style={{
                display: 'inline-block',
                background: isSupervisor ? '#ede9fe' : colors.primaryBg,
                borderRadius: 20,
                padding: '4px 12px',
                fontSize: 12, fontWeight: 700,
                color: isSupervisor ? '#7C3AED' : colors.primary,
                letterSpacing: '0.3px',
              }}>
                {roleLabel}
              </div>
              {classLabel && (
                <div style={{
                  display: 'inline-block',
                  background: '#f0fdf4',
                  borderRadius: 20,
                  padding: '4px 12px',
                  fontSize: 12, fontWeight: 700,
                  color: '#166534',
                  letterSpacing: '0.3px',
                }}>
                  {classLabel}
                </div>
              )}
            </div>
          </div>
        </div>

        <SectionTitle title="CONTACT" />
        <Row label="Email" value={teacher.email} />
        <Row label="Phone" value={teacher.phone} />

        <SectionTitle title="ADDRESS" />
        <Row label="Address" value={teacher.address} />

        <SectionTitle title="ACADEMIC" />
        <Row label="Highest Qualification" value={teacher.qualification} />
        <Row label="Expertise" value={teacher.expertise} />
        {teacher.otherExpertise && (
          <Row label="Other Expertise" value={teacher.otherExpertise} />
        )}

        <SectionTitle title="PERSONAL" />
        <Row label="Date of Birth" value={formatDob(teacher.dob)} />
        <Row label="Gender" value={capitalize(teacher.gender)} />
      </div>

      {/* ── Bottom nav (shared) ── */}
      <BottomNav activeTab="profile" onNavigate={onNavigate} />

      {/* ── Confirmation popup overlay ── */}
      {showConfirm && (
        <div
          onClick={() => setShowConfirm(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              background: '#fff',
              borderRadius: '20px 20px 0 0',
              padding: '28px 24px 44px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            }}
          >
            {/* Icon */}
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: colors.dangerBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 4,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke={colors.danger} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>

            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>
              Sign Out?
            </div>
            <div style={{ fontSize: 14, color: colors.textLight, textAlign: 'center', lineHeight: 1.5 }}>
              Are you sure you want to sign out of your account?
            </div>

            {/* Buttons */}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <button
                onClick={onLogout}
                style={{
                  width: '100%', padding: '15px',
                  background: colors.dangerBg,
                  border: `1.5px solid ${colors.dangerBorder}`,
                  borderRadius: 14,
                  fontSize: 15, fontWeight: 700,
                  color: colors.danger,
                  cursor: 'pointer',
                }}
              >
                Yes, Sign Out
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  width: '100%', padding: '15px',
                  background: colors.primaryBg,
                  border: `1.5px solid ${colors.primary}`,
                  borderRadius: 14,
                  fontSize: 15, fontWeight: 700,
                  color: colors.primary,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;